import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

/**
 * Custom hook to manage filters in URL search params
 * Follows React Router and TanStack best practices
 */
export function useUrlFilters<T extends Record<string, unknown>>(
  defaultFilters: T,
  serialize: (filters: T) => Record<string, string>,
  deserialize: (params: URLSearchParams) => T,
  allFilterKeys?: string[]
) {
  const [searchParams, setSearchParams] = useSearchParams()

  // Get current filters from URL
  const filters = useMemo(() => {
    return deserialize(searchParams)
  }, [searchParams, deserialize])

  // Update filters in URL
  const setFilters = useCallback(
    (newFilters: T | ((prev: T) => T)) => {
      setSearchParams((prev) => {
        const updatedFilters =
          typeof newFilters === "function" ? newFilters(deserialize(prev)) : newFilters

        const serialized = serialize(updatedFilters)
        const newParams = new URLSearchParams(prev)

        // Get all possible filter keys to clear - use provided keys or get from current URL + known keys
        const keysToClear =
          allFilterKeys && allFilterKeys.length > 0
            ? allFilterKeys
            : Array.from(
                new Set([
                  ...Array.from(prev.keys()), // Current URL params
                  ...Object.keys(serialize(defaultFilters)), // Default filter keys
                ])
              )

        // Clear all filter-related params first
        keysToClear.forEach((key) => {
          newParams.delete(key)
        })

        // Set new filter params
        Object.entries(serialized).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            newParams.set(key, String(value))
          }
        })

        return newParams
      })
    },
    [setSearchParams, serialize, deserialize, defaultFilters, allFilterKeys]
  )

  // Reset filters to defaults
  const resetFilters = useCallback(() => {
    setFilters(defaultFilters)
  }, [setFilters, defaultFilters])

  return [filters, setFilters, resetFilters] as const
}

/**
 * Helper to get a single URL param
 */
export function useUrlParam(key: string, defaultValue: string = "") {
  const [searchParams, setSearchParams] = useSearchParams()

  const value = searchParams.get(key) ?? defaultValue

  const setValue = useCallback(
    (newValue: string) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev)
        if (newValue === defaultValue || newValue === "") {
          newParams.delete(key)
        } else {
          newParams.set(key, newValue)
        }
        return newParams
      })
    },
    [key, defaultValue, setSearchParams]
  )

  return [value, setValue] as const
}
